import { action, observable, runInAction } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { $RootStore } from '../../../src/stores/RootStore'
import { WebPageVisibility, WebPageBlockType, WebPageBlockColor, WebPageBlockButtonType } from '../../../graphQL/generated/enums'
import { generateObjectId } from '../../../shared-lib/utils'

export const defaultWebPageData = {
  _id: '',
  title: '',
  path: '',
  group: '',
  order: -1,
  blocks: [],
  visibility: WebPageVisibility.Visible,
  showPageNavigation: true,
  seoSettings: {
    metaTitle: 'Meta title',
    metaDescription: 'Meta description',
  },
  headerBlock: {
    _id: '',
    type: WebPageBlockType.Image,
    color: WebPageBlockColor.Light,
    content: {
      type: WebPageBlockType.Image,
      title: 'Add a page title',
      text: '',
      layout: 1,
      imageUrl: '',
      isPageHeader: true,
      filter: '',
      imageBorderWidth: 0,
      textColor: WebPageBlockColor.Primary,
      shape: 'full-width',
      borderStyle: 'solid',
      textPosition: 'below',
      button: {
        label: 'Book Now',
        url: '/book',
        linkType: WebPageBlockButtonType.BookNow,
        showButton: false,
      },
      secondaryButton: null,
    },
    professionalId: '',
  },
  actionButton: {
    content: {
      label: 'Book now',
      linkType: WebPageBlockButtonType.BookNow,
      showButton: false,
      url: '/book',
    },
  },
}

export type $WebPageModel = WebPageModel

export const PROTECTED_PATHS = ['aftercare', 'termsAndConditions', 'home']

export default class WebPageModel extends ModelStore {
  @observable accessor data: WebPage = { ...defaultWebPageData }
  fetchMethodName = 'webPage'
  saveMethodName = 'createWebPage'
  updateMethodName = 'updateWebPage'
  removeMethodName = 'deleteWebPage'

  constructor(data: Partial<WebPage>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  saveArgs = () => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { _id, headerBlock, ...rest } = this.data
    const { professionalId, ...headerBlockRest } = headerBlock

    const typedBlocks = rest.blocks
      .map(({ professionalId, ...block }) => {
        const { data, ...blockContentRest } = block.content as any
        return {
          ...block,
          content: {
            ...blockContentRest,
            type: block.type,
          },
        }
      })
    /* eslint-enable @typescript-eslint/no-unused-vars */

    // Ensure headerBlock has correct ID when creating a new page
    if (!headerBlockRest._id) {
      headerBlockRest._id = generateObjectId()
    }

    const data = {
      ...rest,
      blocks: typedBlocks,
      headerBlock: headerBlockRest,
    }

    return { data }
  }

  @action setTitle = (title: string) => {
    this.data.title = title
  }

  @action setPath = (path: string) => {
    this.data.path = path
  }

  @action setGroup = (group: string) => {
    this.data.group = group
  }

  @action setSeoSettings = (seoSettings: WebPageSeoSettings | null) => {
    this.data.seoSettings = seoSettings
  }

  @action updateSeoSettings = (key: 'metaTitle' | 'metaDescription', value: string) => {
    if (!this.data.seoSettings) {
      this.data.seoSettings = {
        metaTitle: '',
        metaDescription: '',
      }
    }
    this.data.seoSettings[key] = value
  }

  @action setVisibility = (visibility: WebPageVisibility) => {
    this.data.visibility = visibility
  }

  @action setShowPageNavigation = (show: boolean) => {
    this.data.showPageNavigation = show
  }

  @action setBlocks = (blocks: WebPageBlock[]) => {
    this.data.blocks = blocks
  }

  @action addBlock = (block: WebPageBlock, blockInView?: string) => {

    if (blockInView) {
      // If the block is the header block, add it to the beginning of the blocks array
      const isHeaderBlock = this.data.headerBlock._id === blockInView
      if (isHeaderBlock) {
        runInAction(() => {
          this.data.blocks.unshift(block)
        })
        return
      }

      // Find the target block to insert after
      const foundBlock = this.data.blocks.find(block => block._id === blockInView)
      if (foundBlock) {
        runInAction(() => {
          const index = this.data.blocks.indexOf(foundBlock)
          this.data.blocks.splice(index + 1, 0, block)
        })
        return
      }
    }
    // Otherwise add it to the end of the blocks array
    this.data.blocks.push(block)
  }

  @action updateBlock = (blockId: string, updatedBlock: WebPageBlock, blockInView?: string) => {
    const isHeaderById = this.data.headerBlock._id && (blockId === this.data.headerBlock._id || updatedBlock._id === this.data.headerBlock._id)

    if (isHeaderById) {
      this.data.headerBlock = updatedBlock
      return
    }

    const index = this.data.blocks.findIndex(block => block._id === blockId)
    if (index !== -1) {
      this.data.blocks[index] = updatedBlock
    } else {
      this.addBlock(updatedBlock, blockInView)
    }
  }

  @action removeBlock = (blockId: string) => {
    this.data.blocks = this.data.blocks.filter(block => block._id !== blockId)
  }

  @action reorderBlocks = (blockIds: string[]) => {
    this.data.blocks = blockIds.map(id => {
      return this.data.blocks.find(block => block._id === id)!
    })
  }

  @action createBlock = (type: WebPageBlockType, content: WebPageBlockContent): WebPageBlock => {
    return {
      _id: generateObjectId(),
      professionalId: this.rootStore.userStore.data._id,
      type,
      content: {
        ...content,
        type,
      },
      color: WebPageBlockColor.Light,
    }
  }

  @action updateActionButton = (button: ContentBlockButton) => {
    if (this.data.actionButton) {
      this.data.actionButton.content = button
    } else {
      this.data.actionButton = {
        content: button,
      }
    }
  }

  @action updateWebPage = () => {
    if (this.data._id) {
      return this.update_api()
    }

    this.rootStore.dropdownMessageHandler.showDropdownMessage({
      type: 'error',
      title: 'Failed',
      message: 'There was an issue updating the page',
    })

    return null
  }
}
